<script setup lang="ts">
import { X } from "lucide-vue-next";

const props = defineProps<{
  modelValue: boolean;
  type: "experience" | "education";
  title: string;
  subtitle?: string;
  period?: string;
  description: string;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
}>();

const { t } = useI18n();

let lockedScrollX = 0;
let lockedScrollY = 0;
let restoringScroll = false;
let scrollLocked = false;

const typeLabel = computed(() =>
  props.type === "experience"
    ? t("worker_profile.experience")
    : t("worker_profile.education"),
);

function isModalScrollTarget(target: EventTarget | null) {
  return (
    target instanceof Element &&
    Boolean(target.closest("[data-background-modal-scroll]"))
  );
}

function preventBackgroundScroll(event: Event) {
  if (!scrollLocked || isModalScrollTarget(event.target)) return;
  event.preventDefault();
}

function keepScrollPosition() {
  if (
    !scrollLocked ||
    restoringScroll ||
    (window.scrollX === lockedScrollX && window.scrollY === lockedScrollY)
  ) {
    return;
  }

  restoringScroll = true;
  window.scrollTo(lockedScrollX, lockedScrollY);

  requestAnimationFrame(() => {
    restoringScroll = false;
  });
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key !== "Escape") return;

  event.preventDefault();
  close();
}

function lockPageScroll() {
  if (!import.meta.client || scrollLocked) return;

  lockedScrollX = window.scrollX;
  lockedScrollY = window.scrollY;
  scrollLocked = true;

  window.addEventListener("keydown", handleKeydown);
  window.addEventListener("wheel", preventBackgroundScroll, {
    passive: false,
  });
  window.addEventListener("touchmove", preventBackgroundScroll, {
    passive: false,
  });
  window.addEventListener("scroll", keepScrollPosition, {
    passive: true,
  });
}

function unlockPageScroll() {
  if (!import.meta.client || !scrollLocked) return;

  scrollLocked = false;

  window.removeEventListener("keydown", handleKeydown);
  window.removeEventListener("wheel", preventBackgroundScroll);
  window.removeEventListener("touchmove", preventBackgroundScroll);
  window.removeEventListener("scroll", keepScrollPosition);

  window.scrollTo(lockedScrollX, lockedScrollY);
}

function close() {
  emit("update:modelValue", false);
}

watch(
  () => props.modelValue,
  open => {
    if (open) {
      lockPageScroll();
    }
  },
  { flush: "sync", immediate: true },
);

function afterLeave() {
  if (!props.modelValue) {
    unlockPageScroll();
  }
}

onBeforeUnmount(() => {
  unlockPageScroll();
});
</script>

<template>
  <ClientOnly>
    <Teleport to="body">
      <Transition name="background-details" appear @after-leave="afterLeave">
        <div
          v-if="modelValue"
          class="fixed inset-0 z-[5050] flex items-center justify-center bg-black/55 p-4 backdrop-blur-[1px] sm:p-6"
          role="presentation"
          @click.self="close"
        >
          <section
            role="dialog"
            aria-modal="true"
            :aria-label="title"
            class="background-details-panel relative flex max-h-[min(82dvh,680px)] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
            @click.stop
          >
            <button
              type="button"
              class="absolute right-4 top-4 z-10 flex size-10 items-center justify-center rounded-full bg-gray-100 text-app-text transition hover:bg-gray-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
              :aria-label="$t('worker_profile.close_background_details')"
              @click="close"
            >
              <X class="size-5" />
            </button>

            <div
              data-background-modal-scroll
              class="background-details-scroll min-h-0 overflow-y-auto overscroll-contain px-5 py-6 sm:px-7 sm:py-7"
            >
              <p
                class="pr-12 text-xs font-bold uppercase tracking-[0.12em] text-primary"
              >
                {{ typeLabel }}
              </p>

              <h2
                class="mt-2 break-words pr-12 text-2xl font-bold leading-tight text-app-text"
              >
                {{ title }}
              </h2>

              <div class="mt-3 flex flex-wrap items-center gap-2">
                <p
                  v-if="subtitle"
                  class="break-words text-sm font-medium text-app-muted"
                >
                  {{ subtitle }}
                </p>

                <time
                  v-if="period"
                  class="inline-flex rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold text-primary"
                >
                  {{ period }}
                </time>
              </div>

              <div class="mt-6 border-t border-app-border pt-5">
                <p
                  class="whitespace-pre-wrap break-words text-sm leading-7 text-app-text/80 [overflow-wrap:anywhere] sm:text-base"
                >
                  {{ description }}
                </p>
              </div>
            </div>
          </section>
        </div>
      </Transition>
    </Teleport>
  </ClientOnly>
</template>

<style scoped>
.background-details-enter-active,
.background-details-leave-active,
.background-details-appear-active {
  transition: opacity 180ms ease;
}

.background-details-enter-active .background-details-panel,
.background-details-leave-active .background-details-panel,
.background-details-appear-active .background-details-panel {
  transition:
    transform 220ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 180ms ease;
}

.background-details-enter-from,
.background-details-appear-from,
.background-details-leave-to {
  opacity: 0;
}

.background-details-enter-from .background-details-panel,
.background-details-appear-from .background-details-panel {
  opacity: 0;
  transform: translateY(12px) scale(0.985);
}

.background-details-leave-to .background-details-panel {
  opacity: 0;
  transform: translateY(8px) scale(0.99);
}

.background-details-scroll {
  scrollbar-color: rgb(148 163 184 / 0.72) transparent;
  scrollbar-width: thin;
}

.background-details-scroll::-webkit-scrollbar {
  width: 8px;
}

.background-details-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.background-details-scroll::-webkit-scrollbar-thumb {
  min-height: 44px;
  border: 2px solid transparent;
  border-radius: 999px;
  background: rgb(148 163 184 / 0.72);
  background-clip: content-box;
}

@media (prefers-reduced-motion: reduce) {
  .background-details-enter-active,
  .background-details-leave-active,
  .background-details-appear-active,
  .background-details-enter-active .background-details-panel,
  .background-details-leave-active .background-details-panel,
  .background-details-appear-active .background-details-panel {
    transition-duration: 1ms;
  }
}
</style>
